import { Clients } from '@/api/Clients';
import {
  ClientRequest,
  ClientResponse,
  ClientsRequest,
  ExternalOrgCommercialTermsRequest,
  ExternalOrgCommercialTermsResponse,
  PageResponseClientListItemResponse,
} from '@/api/data-contracts';
import useUploadProgressService from '@/shared/api/uploadProgress.service';

export interface ClientsFilters {
  search?: string;
  hasTenancy?: boolean;
  source?: 'INTERNAL' | 'EXTERNAL';
  groupBy?: 'ORGANIZATION';
}

const useClientsService = () => {
  const clientsApi = useUploadProgressService(Clients);

  const getClients = async (
    query: {
      request: ClientsRequest;
      filters?: ClientsFilters;
    },
    signal: AbortSignal | undefined,
  ): Promise<PageResponseClientListItemResponse> => {
    const { data } = await clientsApi.getClients(query.request, query.filters, {
      signal,
    });
    return data;
  };

  const getClientDetails = async (
    id: string,
    signal: AbortSignal | undefined,
  ): Promise<ClientResponse> => {
    const { data } = await clientsApi.showClient(id, { signal });
    return data;
  };

  const createClient = async (form: ClientRequest): Promise<ClientResponse> => {
    const { data } = await clientsApi.addClient(form);
    return data;
  };

  const updateClient = async (clientId: string, form: ClientRequest): Promise<ClientResponse> => {
    const { data } = await clientsApi.updateClient(clientId, form);
    return data;
  };

  const deleteClient = async (clientId: string): Promise<void> => {
    await clientsApi.deleteClient(clientId);
  };

  const getExternalOrgCommercialTerms = async (
    advertiserOrganizationId: string,
    signal?: AbortSignal,
  ): Promise<ExternalOrgCommercialTermsResponse> => {
    const { data } = await clientsApi.getExternalOrgCommercialTerms(advertiserOrganizationId, {
      signal,
    });
    return data;
  };

  const setExternalOrgCommercialTerms = async (
    advertiserOrganizationId: string,
    request: ExternalOrgCommercialTermsRequest,
  ): Promise<ExternalOrgCommercialTermsResponse> => {
    const { data } = await clientsApi.setExternalOrgCommercialTerms(
      advertiserOrganizationId,
      request,
    );
    return data;
  };

  return {
    getClients,
    getClientDetails,
    createClient,
    updateClient,
    deleteClient,
    getExternalOrgCommercialTerms,
    setExternalOrgCommercialTerms,
  };
};

export default useClientsService;
